/* ===================================================================
 * Estimativa de Entrega por CEP — custom element <delivery-estimate>
 *
 * - No load: detecta cidade/UF do visitante via IP (cacheado em
 *   localStorage), resolve a regiao de entrega e mostra:
 *     "Estimativa de entrega ate {data} em {cidade}/{UF}"
 *     "Comprando dentro das proximas {Xh Ymin}." (se antes do corte)
 * - Link "Calcular para outro CEP" abre um <dialog> onde o cliente
 *   digita o CEP. BrasilAPI resolve cidade/UF -> recalcula e persiste.
 *
 * Config (regioes + cortes) vem de <script class="delivery-estimate__config">
 * Strings i18n vem de <script class="delivery-estimate__i18n">
 * Ambos renderizados pelo snippet delivery-estimate.liquid.
 * =================================================================== */

(function () {
  'use strict';

  var GEO_CACHE_KEY = 'kl_de_geo';
  var CEP_CACHE_KEY = 'kl_de_cep';
  var GEO_TTL = 12 * 60 * 60 * 1000;      // 12h
  var CEP_TTL = 30 * 24 * 60 * 60 * 1000; // 30 dias

  function normalize(str) {
    return (str || '')
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  function safeParse(str, fallback) {
    try { return JSON.parse(str); } catch (e) { return fallback; }
  }

  function readCache(key, ttl) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.ts || (Date.now() - obj.ts) > ttl) return null;
      return obj;
    } catch (e) { return null; }
  }

  function writeCache(key, data) {
    try {
      data.ts = Date.now();
      localStorage.setItem(key, JSON.stringify(data));
    } catch (e) { /* storage cheio/bloqueado — ignora */ }
  }

  // ---- Datas / dias uteis ----
  function isBusinessDay(d) {
    var g = d.getDay();
    return g !== 0 && g !== 6; // 0=dom, 6=sab
  }

  function addBusinessDays(date, n) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      if (isBusinessDay(d)) added++;
    }
    return d;
  }

  function nextBusinessDay(date) {
    var d = new Date(date.getTime());
    do { d.setDate(d.getDate() + 1); } while (!isBusinessDay(d));
    return d;
  }

  function startOfDay(date) {
    var d = new Date(date.getTime());
    d.setHours(0, 0, 0, 0);
    return d;
  }

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      var cfgEl = this.querySelector('.delivery-estimate__config');
      var i18nEl = this.querySelector('.delivery-estimate__i18n');
      this.config = cfgEl ? safeParse(cfgEl.textContent, null) : null;
      this.i18n = i18nEl ? safeParse(i18nEl.textContent, {}) : {};

      if (!this.config || !Array.isArray(this.config.regioes)) {
        // Sem config valido — nao renderiza nada
        this.style.display = 'none';
        return;
      }

      // sort cortes asc
      this.config.cortes = (this.config.cortes || []).slice().sort(function (a, b) {
        return (a.hora * 60 + (a.minuto || 0)) - (b.hora * 60 + (b.minuto || 0));
      });

      // refs DOM
      this.$estimate = this.querySelector('[data-de-estimate]');
      this.$cutoff = this.querySelector('[data-de-cutoff]');
      this.$openBtn = this.querySelector('[data-de-open]');
      this.$modal = this.querySelector('[data-de-modal]');
      this.$cepInput = this.querySelector('[data-de-cep-input]');
      this.$cepBtn = this.querySelector('[data-de-cep-btn]');
      this.$cepResult = this.querySelector('[data-de-cep-result]');
      this.$closeBtns = this.querySelectorAll('[data-de-close]');

      this.bindEvents();
      this.loadLocation();
    }

    disconnectedCallback() {
      if (this._timer) clearInterval(this._timer);
    }

    t(key) {
      return this.i18n && this.i18n[key] != null ? this.i18n[key] : '';
    }

    bindEvents() {
      var self = this;
      if (this.$openBtn) {
        this.$openBtn.addEventListener('click', function () { self.openModal(); });
      }
      if (this.$closeBtns) {
        this.$closeBtns.forEach(function (b) {
          b.addEventListener('click', function () { self.closeModal(); });
        });
      }
      if (this.$modal) {
        // fechar clicando no backdrop
        this.$modal.addEventListener('click', function (e) {
          if (e.target === self.$modal) self.closeModal();
        });
      }
      if (this.$cepInput) {
        this.$cepInput.addEventListener('input', function (e) {
          var v = e.target.value.replace(/\D/g, '').slice(0, 8);
          if (v.length > 5) v = v.slice(0, 5) + '-' + v.slice(5);
          e.target.value = v;
        });
        this.$cepInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); self.calcCep(); }
        });
      }
      if (this.$cepBtn) {
        this.$cepBtn.addEventListener('click', function () { self.calcCep(); });
      }
    }

    // ---- Localizacao ----
    loadLocation() {
      var self = this;
      // 1. CEP salvo manualmente tem prioridade
      var cep = readCache(CEP_CACHE_KEY, CEP_TTL);
      if (cep && cep.uf) {
        this.applyLocation(cep);
        return;
      }
      // 2. Geo por IP cacheado
      var geo = readCache(GEO_CACHE_KEY, GEO_TTL);
      if (geo && geo.uf) {
        this.applyLocation(geo);
        return;
      }
      // 3. Buscar geo por IP (assincrono); enquanto isso mostra fallback
      this.applyLocation(null);
      this.fetchGeo().then(function (loc) {
        if (loc && loc.uf) {
          writeCache(GEO_CACHE_KEY, loc);
          // so sobrescreve se ainda nao veio CEP manual nesse meio tempo
          if (!readCache(CEP_CACHE_KEY, CEP_TTL)) self.applyLocation(loc);
        }
      }).catch(function () { /* mantem fallback */ });
    }

    fetchGeo() {
      // ipwho.is: gratis, sem chave, CORS, retorna city + region_code (UF)
      return fetch('https://ipwho.is/?fields=success,city,region,region_code,country_code')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (d && d.success !== false && d.region_code) {
            return { city: d.city || '', uf: d.region_code, source: 'ip' };
          }
          // fallback: ipapi.co
          return fetch('https://ipapi.co/json/')
            .then(function (r) { return r.json(); })
            .then(function (d2) {
              if (d2 && d2.region_code) {
                return { city: d2.city || '', uf: d2.region_code, source: 'ip' };
              }
              return null;
            });
        });
    }

    // ---- Resolucao de regiao ----
    resolveRegion(uf, city) {
      uf = (uf || '').toUpperCase();
      var cityN = normalize(city);
      var regs = this.config.regioes;
      for (var i = 0; i < regs.length; i++) {
        var r = regs[i];
        // match por uf + cidade especifica (ex: SP capital)
        if (r.uf) {
          if (r.uf.toUpperCase() !== uf) continue;
          if (r.cidade && normalize(r.cidade) !== cityN) continue;
          return r;
        }
        // match por lista de UFs
        if (Array.isArray(r.ufs) && r.ufs.indexOf(uf) !== -1) return r;
      }
      // padrao
      var def = null;
      for (var k = 0; k < regs.length; k++) {
        if (regs[k].id === this.config.regiao_padrao) { def = regs[k]; break; }
      }
      return def || regs[0];
    }

    // ---- Calculo ----
    computeEstimate(region) {
      var now = new Date();
      var cortes = this.config.cortes || [];

      // O pedido "comeca a contar" hoje se for dia util E antes do ultimo
      // corte; senao, comeca no proximo dia util. (Os prazos regionais ja
      // incluem producao — ver config.) buffer_dias permite empurrar a
      // estimativa em picos de demanda.
      var startsToday = false;
      if (cortes.length && isBusinessDay(now)) {
        var last = cortes[cortes.length - 1];
        var lastToday = new Date(now.getTime());
        lastToday.setHours(last.hora, last.minuto || 0, 0, 0);
        startsToday = now < lastToday;
      }
      var start = startsToday ? startOfDay(now) : nextBusinessDay(now);

      var buffer = parseInt(this.config.buffer_dias, 10) || 0;
      var maxDays = ((region && region.max != null) ? region.max : 10) + buffer;
      var deliveryDate = addBusinessDays(start, maxDays);

      return { deliveryDate: deliveryDate, nextCutoff: this.computeNextCutoff(now, cortes) };
    }

    // Proximo corte no CALENDARIO (hoje, ou primeiro corte de amanha).
    // Contador = gatilho de urgencia puro: NAO usa logica de dia util de
    // proposito. Comprar no fim de semana tem impacto real (o pedido entra
    // na esteira no primeiro expediente). A logica de dia util fica so na
    // DATA de entrega (computeEstimate), nao aqui.
    computeNextCutoff(now, cortes) {
      if (!cortes.length) return null;
      for (var i = 0; i < cortes.length; i++) {
        var t = new Date(now.getTime());
        t.setHours(cortes[i].hora, cortes[i].minuto || 0, 0, 0);
        if (t > now) return t;
      }
      // todos os cortes de hoje ja passaram -> primeiro corte de amanha
      var d = new Date(now.getTime());
      d.setDate(d.getDate() + 1);
      d.setHours(cortes[0].hora, cortes[0].minuto || 0, 0, 0);
      return d;
    }

    // ---- Render ----
    applyLocation(loc) {
      // loc = {city, uf} ou null
      var region;
      var localText = '';
      if (loc && loc.uf) {
        region = this.resolveRegion(loc.uf, loc.city);
        localText = loc.city ? (loc.city + '/' + loc.uf.toUpperCase()) : loc.uf.toUpperCase();
      } else {
        // sem localizacao: usa regiao padrao, sem texto de local
        for (var k = 0; k < this.config.regioes.length; k++) {
          if (this.config.regioes[k].id === this.config.regiao_padrao) { region = this.config.regioes[k]; break; }
        }
        region = region || this.config.regioes[0];
      }

      this._region = region;
      this._localText = localText;
      this.renderEstimate();

      // contador atualiza a cada 30s
      if (this._timer) clearInterval(this._timer);
      var self = this;
      this._timer = setInterval(function () { self.renderEstimate(); }, 30000);
    }

    renderEstimate() {
      if (!this._region || !this.$estimate) return;
      var est = this.computeEstimate(this._region);
      var dateStr = this.formatDate(est.deliveryDate);

      // Linha 1: "Estimativa de entrega ate {data} em {local}"
      var line1 = this.t('estimate_prefix') + ' ' + '<strong>' + dateStr + '</strong>';
      if (this._localText) {
        line1 += ' ' + this.t('estimate_in') + ' ' + '<strong>' + escapeHtml(this._localText) + '</strong>';
      }
      this.$estimate.innerHTML = line1;

      // Linha 2: contador para o proximo corte (so quando faltar < 24h).
      // Some quando nao ha corte proximo (ex: fim de semana distante).
      if (this.$cutoff) {
        var show = false;
        if (est.nextCutoff) {
          var remaining = est.nextCutoff.getTime() - Date.now();
          if (remaining > 0 && remaining < 24 * 60 * 60 * 1000) {
            this.$cutoff.innerHTML = this.t('buying_within') + ' <strong>' + this.formatCountdown(remaining) + '</strong>.';
            show = true;
          }
        }
        this.$cutoff.hidden = !show;
      }
    }

    formatDate(date) {
      var locale = (document.documentElement.lang || 'pt-BR');
      try {
        return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date);
      } catch (e) {
        return date.getDate() + '/' + (date.getMonth() + 1);
      }
    }

    formatCountdown(ms) {
      if (ms < 0) ms = 0;
      var totalMin = Math.floor(ms / 60000);
      var h = Math.floor(totalMin / 60);
      var m = totalMin % 60;
      var parts = [];
      if (h > 0) parts.push(h + ' ' + (h === 1 ? this.t('hour_one') : this.t('hour_other')));
      if (m > 0) parts.push(m + ' ' + (m === 1 ? this.t('minute_one') : this.t('minute_other')));
      if (parts.length === 0) parts.push('1 ' + this.t('minute_one'));
      return parts.join(' ' + this.t('and') + ' ');
    }

    // ---- Modal CEP ----
    openModal() {
      if (!this.$modal) return;
      if (this.$cepResult) this.$cepResult.innerHTML = '';
      if (typeof this.$modal.showModal === 'function') {
        try { this.$modal.showModal(); } catch (e) { this.$modal.setAttribute('open', ''); }
      } else {
        this.$modal.setAttribute('open', '');
      }
      var self = this;
      requestAnimationFrame(function () { if (self.$cepInput) self.$cepInput.focus(); });
    }

    closeModal() {
      if (!this.$modal) return;
      if (typeof this.$modal.close === 'function' && this.$modal.open) {
        try { this.$modal.close(); } catch (e) { this.$modal.removeAttribute('open'); }
      } else {
        this.$modal.removeAttribute('open');
      }
    }

    calcCep() {
      var self = this;
      if (!this.$cepInput) return;
      var cep = this.$cepInput.value.replace(/\D/g, '');
      if (cep.length !== 8) {
        this.setCepResult('<span class="delivery-estimate__error">' + escapeHtml(this.t('cep_invalid')) + '</span>');
        return;
      }
      this.setCepResult('<span class="delivery-estimate__loader" aria-hidden="true"></span> ' + escapeHtml(this.t('calculating')));

      // Delay minimo (~1s) pra o "Calculando..." ser perceptivel e nao
      // parecer estatico/instantaneo. Sucesso E erro esperam o delay.
      // BrasilAPI v2: agrega varios provedores (Correios + ViaCEP + ...).
      // 200 -> { state, city, neighborhood, street, ... }
      // erro (4xx/5xx) -> { name, message, type }, tratado via r.ok.
      var minDelay = new Promise(function (res) { setTimeout(res, 1000); });
      var lookup = fetch('https://brasilapi.com.br/api/cep/v2/' + cep)
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });

      Promise.all([lookup, minDelay]).then(function (arr) {
        var d = arr[0];
        if (!d || !d.state) {
          self.setCepResult('<span class="delivery-estimate__error">' + escapeHtml(self.t('cep_not_found')) + '</span>');
          return;
        }
        var loc = { city: d.city || '', uf: d.state, bairro: d.neighborhood || '', cep: cep, source: 'cep' };
        writeCache(CEP_CACHE_KEY, loc);
        self.applyLocation(loc);

        var region = self.resolveRegion(loc.uf, loc.city);
        var est = self.computeEstimate(region);
        var dateStr = self.formatDate(est.deliveryDate);
        // Exibe bairro quando a BrasilAPI retornar (ex: "Centro, Toledo/PR")
        var localText = (loc.city ? loc.city + '/' + loc.uf : loc.uf);
        if (loc.bairro) localText = loc.bairro + ', ' + localText;
        self.setCepResult(
          '<div class="delivery-estimate__result-ok">' +
            '<p class="delivery-estimate__result-local">' + escapeHtml(localText) + '</p>' +
            '<p class="delivery-estimate__result-date">' + escapeHtml(self.t('result_prefix')) + ' <strong>' + dateStr + '</strong></p>' +
          '</div>'
        );
      });
    }

    setCepResult(html) {
      if (this.$cepResult) this.$cepResult.innerHTML = html;
    }
  }

  function escapeHtml(s) {
    return (s || '').toString()
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  if (!window.customElements.get('delivery-estimate')) {
    window.customElements.define('delivery-estimate', DeliveryEstimate);
  }
})();
